/// <amd-module name="Release/Landing/LeftPanel/ActivePipelines/ActivePipelinesActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { localeFormat } from "VSS/Core/Util/String";

import { IReleaseService } from "Release/Client/Service/Release";
import { ReleaseDefinition, Deployment, ReleaseEnvironment, ReleaseEnvironmentExpands } from "Release/Client/Contracts/Release";
import { ActionCreatorBase, Action } from "DistributedTask/Flux/Actions";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { ActivePipelinesActions, IDefinitionAndDeploymentPayload } from "./ActivePipelinesActions";
import { IReleaseHubServiceDataService } from "Release/Landing/PageData";
import { SelectionActions } from "Release/Landing/Selection/SelectionActions";
import { IActiveDefinitionReference } from "DataProviderContracts";
import { MessageActions } from "HubMessageBar/MessageActions";
import { MessageBarKeys, QueryParamKeys } from "Constants";
import * as Resources from "Release/Landing/Resources";
import { getActiveDefinitionReferenceMatch } from "LandingUtils";

export class ActivePipelinesActionCreator extends ActionCreatorBase {
    public static getKey(): string {
        return "release-landing-active-pipelines-actioncreator";
    }

    public initialize(): void {
        this._actions = ActionsHubManager.GetActionsHub<ActivePipelinesActions>(ActivePipelinesActions);
    }

    public initializeDefs(pageContext: IVssPageContext, preExistingRecentDefinitions: IActiveDefinitionReference[], searchText: string): void {
        const rmDataService = pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");
        let activeDefinitions = rmDataService.getActiveDefinitionReferences();
        let recentDefinitions = rmDataService.getRecentDefinitionReferences();
        if (activeDefinitions && recentDefinitions) {
            this._initializePipelines(pageContext, activeDefinitions, recentDefinitions, preExistingRecentDefinitions, searchText);
        }
        else {
            Promise.all([rmDataService.getActiveDefinitionReferencesAsync(), rmDataService.getRecentDefinitionReferencesAsync()]).then((promiseDataValues) => {
                let [activeDefinitions, recentDefinitions] = promiseDataValues;
                if (activeDefinitions && recentDefinitions) {
                    this._initializePipelines(pageContext, activeDefinitions, recentDefinitions, preExistingRecentDefinitions, searchText);
                }
            });
        }
    }

    public handleAdditionalDefSelection(pageContext: IVssPageContext, definition: ReleaseDefinition | undefined, definitionReference: IActiveDefinitionReference | undefined): void {
        this._getLastDeploymentAndInvoke(pageContext, definition, definitionReference, this._actions.selectAdditionalPipeline);
    }

    public handleOnFavoriteReleaseDefinition(pageContext: IVssPageContext, definition: ReleaseDefinition): void {
        this._getLastDeploymentAndInvoke(pageContext, definition, undefined, this._actions.onPipelineFavorited);
    }

    private _getLastDeploymentAndInvoke(pageContext: IVssPageContext, definition: ReleaseDefinition | undefined, definitionReference: IActiveDefinitionReference | undefined, action: Action<IDefinitionAndDeploymentPayload>): void {
        let definitionId = definition ? definition.id : (definitionReference ? definitionReference.id : -1);

        if (definitionId === -1) {
            return;
        }

        pageContext.getService<IReleaseService>("IReleaseService").getLastDeployment(definitionId)
            .then((deployment: Deployment | undefined) => {
                action.invoke({
                    definition: definition,
                    deployment: deployment,
                    definitionReference: definitionReference
                });
            });
    }

    private _initializePipelines(
        pageContext: IVssPageContext,
        activeDefinitions: IActiveDefinitionReference[],
        recentDefinitions: IActiveDefinitionReference[],
        preExistingRecentDefinitions: IActiveDefinitionReference[],
        searchText: string): void {
        const extraPreExistingDefinitions = preExistingRecentDefinitions.filter(
            (def) => !getActiveDefinitionReferenceMatch(activeDefinitions, def.id) && !getActiveDefinitionReferenceMatch(recentDefinitions, def.id));
        recentDefinitions.unshift(...extraPreExistingDefinitions);

        const historyState = pageContext.getService<IVssHistoryService>("IVssHistoryService").getState();
        const definitionIdKey = QueryParamKeys.DefinitionIdKey;
        const historyDefinitionId = (historyState[definitionIdKey] && parseInt(historyState[definitionIdKey], 10)) || -1;

        const selectFirst: (definitions: IActiveDefinitionReference[]) => IActiveDefinitionReference | null | undefined = (definitions: IActiveDefinitionReference[]) => definitions[0];
        let selectDefinitionPredicate: (definitions: IActiveDefinitionReference[]) => IActiveDefinitionReference | null | undefined = selectFirst;
        if (historyDefinitionId > 0) {
            selectDefinitionPredicate = (definitions: IActiveDefinitionReference[]) => definitions.find((def) => def.id === historyDefinitionId);
        }

        const allPipelines = [...(activeDefinitions || []), ...(recentDefinitions || [])];
        let selectedDefinition = selectDefinitionPredicate(allPipelines);

        if (historyDefinitionId > 0 && !selectedDefinition) {
            ActionsHubManager.GetActionsHub<MessageActions>(MessageActions).setMessage.invoke({ key: MessageBarKeys.HubError, value: localeFormat(Resources.PipelineIdNotFound, historyDefinitionId) });
            selectedDefinition = selectFirst(allPipelines);
        }

        this._actions.initializeDefs.invoke({
            activeDefinitions: activeDefinitions,
            recentDefinitions: recentDefinitions
        });

        // Do not raise selection action on initializing search view
        if (selectedDefinition && !searchText) {
            ActionsHubManager.GetActionsHub<SelectionActions>(SelectionActions).selectDefinition.invoke({
                id: selectedDefinition.id,
                folderId: "",
                noSelection: false
            });
        }
    }

    public handleDeploymentStarted(pageContext: IVssPageContext, definitionId: number, releaseId: number, releaseEnvironmentId: number): void {
        pageContext.getService<IReleaseService>("IReleaseService").getReleaseEnvironment(releaseId, releaseEnvironmentId, ReleaseEnvironmentExpands.None)
            .then((releaseEnvironment: ReleaseEnvironment) => {
                this._actions.handleDeployJobStarted.invoke({ definitionId: definitionId, environment: releaseEnvironment });
            });
    }

    private _actions: ActivePipelinesActions;
}